import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = {
  key: 0,
  class: "mb-4 text-sm"
}
const _hoisted_3 = { class: "ml-4 list-disc text-gray-600 dark:text-gray-400" }
const _hoisted_4 = { class: "mb-1" }
const _hoisted_5 = { class: "mb-1" }
const _hoisted_6 = { class: "mb-4 flex items-center justify-between rounded-lg border p-2 dark:border-gray-800" }
const _hoisted_7 = { class: "flex h-full items-center gap-4" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "font-medium text-black dark:text-white" }
const _hoisted_10 = { class: "text-secondary text-sm" }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { class: "font-medium text-black dark:text-white" }
const _hoisted_13 = /*#__PURE__*/_createElementVNode("div", { class: "flex h-full flex-row items-center justify-center rounded-r-lg border-gray-100 px-2 dark:border-gray-800" }, [
  /*#__PURE__*/_createElementVNode("span", { class: "text-xl text-black dark:text-white" }, "%")
], -1)
const _hoisted_14 = {
  key: 0,
  class: "text-secondary mt-2 text-sm"
}
const _hoisted_15 = { class: "mt-4" }

import { TransactionReceipt } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { format, formatDistanceToNow } from 'date-fns';
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BalForm from '@/components/_global/BalForm/BalForm.vue';
import BalTextInput from '@/components/_global/BalTextInput/BalTextInput.vue';
import ConfirmationIndicator from '@/components/web3/ConfirmationIndicator.vue';
import useEthers from '@/composables/useEthers';
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import {
  dateTimeLabelFor,
  toJsTimestamp,
  toUtcTime,
} from '@/composables/useTime';
import useTransactions from '@/composables/useTransactions';
import useVeBal from '@/composables/useVeBAL';
import { WEIGHT_VOTE_DELAY } from '@/constants/gauge-controller';
import { VEBAL_VOTING_GAUGE } from '@/constants/voting-gauges';
import { bnum, isSameAddress, scale } from '@/lib/utils';
import { isPositive } from '@/lib/utils/validations';
import { VeBalLockInfo } from '@/services/balancer/contracts/contracts/veBAL';
import { VotingGaugeWithVotes } from '@/services/balancer/gauges/gauge-controller.decorator';
import { gaugeControllerService } from '@/services/contracts/gauge-controller.service';
import { WalletError } from '@/types';
import { TransactionActionState } from '@/types/transactions';

/**
 * TYPES
 */
type Props = {
  gauge: VotingGaugeWithVotes;
  unallocatedVoteWeight: number;
  logoURIs: string[];
  poolURL: string;
  veBalLockInfo?: VeBalLockInfo;
};


export default _defineComponent({
  props: {
    gauge: { type: null, required: true },
    unallocatedVoteWeight: { type: Number, required: true },
    logoURIs: { type: Array, required: true },
    poolURL: { type: String, required: true },
    veBalLockInfo: { type: null, required: false }
  } as unknown as undefined,
  emits: ["close", "success"] as unknown as undefined,
  setup(__props: {
  gauge: VotingGaugeWithVotes;
  unallocatedVoteWeight: number;
  logoURIs: string[];
  poolURL: string;
  veBalLockInfo?: VeBalLockInfo;
}, { emit }: { emit: ({
  (e: 'close'): void;
  (e: 'success'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props
const MINIMUM_LOCK_TIME = 86_400_000 * 7;

/**
 * PROPS & EMITS
 */




/**
 * COMPOSABLES
 */
const { fNum2 } = useNumbers();
const { t } = useI18n();
const { addTransaction } = useTransactions();
const { txListener, getTxConfirmedAt } = useEthers();
const { veBalBalance } = useVeBal();

/**
 * STATE
 */
const voteWeight = ref<string>('');
const voteState = reactive<TransactionActionState>({
  init: false,
  confirming: false,
  confirmed: false,
  confirmedAt: '',
});

/**
 * COMPUTED
 */
const voteButtonDisabled = computed((): boolean => {
  if (isVeBalGauge.value) {
    return !!voteError.value || !hasEnoughVotes.value;
  }

  return !!voteWarning.value || !!voteError.value || !hasEnoughVotes.value;
});

const voteInputDisabled = computed((): boolean => {
  return !!voteError.value;
});

const currentWeight = computed(() => props.gauge.userVotes);
const currentWeightNormalized = computed(() =>
  scale(bnum(currentWeight.value), -2).toString()
);
const hasVotes = computed((): boolean => bnum(currentWeight.value).gt(0));

const isVeBalGauge = computed((): boolean =>
  isSameAddress(props.gauge.address, VEBAL_VOTING_GAUGE?.address || '')
);

// Is votes next period value above 10%?
const votesNextPeriodGt10pct = computed((): boolean => {
  const gaugeVoteWeightNormalized = scale(props.gauge.votesNextPeriod, -18);
  return gaugeVoteWeightNormalized.gte(bnum('0.1'));
});

const voteTitle = computed(() =>
  hasVotes.value
    ? t('veBAL.liquidityMining.popover.title.edit')
    : t('veBAL.liquidityMining.popover.title.vote')
);

const voteButtonText = computed(() =>
  hasVotes.value
    ? t('veBAL.liquidityMining.popover.button.edit')
    : t('veBAL.liquidityMining.popover.button.vote')
);

const votedToRecentlyWarning = computed(() => {
  const lastUserVoteTime = toJsTimestamp(props.gauge.lastUserVoteTime);
  if (Date.now() < lastUserVoteTime + WEIGHT_VOTE_DELAY) {
    const remainingTime = formatDistanceToNow(
      lastUserVoteTime + WEIGHT_VOTE_DELAY
    );
    return {
      title: t('veBAL.liquidityMining.popover.warnings.votedTooRecently.title'),
      description: t(
        'veBAL.liquidityMining.popover.warnings.votedTooRecently.description',
        [remainingTime]
      ),
    };
  }
  return null;
});

const voteLockedUntilText = computed<string>(() => {
  const unlockTime = Date.now() + WEIGHT_VOTE_DELAY;
  return format(toUtcTime(new Date(unlockTime)), 'd LLLL y');
});

const noVeBalWarning = computed(() => {
  if (Number(veBalBalance.value) > 0) {
    return null;
  }
  return {
    title: t('veBAL.liquidityMining.popover.warnings.noVeBal.title'),
    description: t(
      'veBAL.liquidityMining.popover.warnings.noVeBal.description'
    ),
  };
});

const veBalLockTooShortWarning = computed(() => {
  if (props.veBalLockInfo?.hasExistingLock && !props.veBalLockInfo?.isExpired) {
    const lockEndDate = props.veBalLockInfo.lockedEndDate;
    if (lockEndDate < Date.now() + MINIMUM_LOCK_TIME) {
      return {
        title: t(
          'veBAL.liquidityMining.popover.warnings.veBalLockTooShort.title'
        ),
        description: t(
          'veBAL.liquidityMining.popover.warnings.veBalLockTooShort.description'
        ),
      };
    }
  }

  return null;
});

const veBalVoteOverLimitWarning = computed(() => {
  if (isVeBalGauge.value && votesNextPeriodGt10pct.value) {
    return {
      title: t(
        'veBAL.liquidityMining.popover.warnings.veBalVoteOverLimitWarning.title'
      ),
      description: t(
        'veBAL.liquidityMining.popover.warnings.veBalVoteOverLimitWarning.description'
      ),
    };
  }

  return null;
});

const voteWarning = computed(
  (): {
    title: string;
    description: string;
  } | null => {
    if (veBalVoteOverLimitWarning.value) return veBalVoteOverLimitWarning.value;
    return null;
  }
);

const voteError = computed(
  (): {
    title: string;
    description: string;
  } | null => {
    if (votedToRecentlyWarning.value) return votedToRecentlyWarning.value;
    if (noVeBalWarning.value) return noVeBalWarning.value;
    if (veBalLockTooShortWarning.value) return veBalLockTooShortWarning.value;
    if (voteState.error) return voteState.error;
    return null;
  }
);

const transactionInProgress = computed(
  (): boolean => voteState.init || voteState.confirming
);

const hasEnoughVotes = computed((): boolean => {
  return isVoteWeightValid(voteWeight.value);
});

const unallocatedVotesFormatted = computed((): string =>
  fNum2(
    scale(bnum(props.unallocatedVoteWeight), -4).toString(),
    FNumFormats.percent
  )
);

const unallocatedVotesClass = computed(() => {
  return hasEnoughVotes.value
    ? ['text-gray-600 dark:text-gray-400']
    : ['text-red-600'];
});

const remainingVotes = computed(() => {
  let remainingVotesText;
  if (!hasEnoughVotes.value) {
    remainingVotesText = 'veBAL.liquidityMining.popover.remainingVotesExceeded';
  } else {
    remainingVotesText = hasVotes.value
      ? 'veBAL.liquidityMining.popover.remainingVotesEditing'
      : 'veBAL.liquidityMining.popover.remainingVotes';
  }
  const remainingVotesFormatted = fNum2(
    scale(
      bnum(props.unallocatedVoteWeight).plus(bnum(currentWeight.value)),
      -4
    ).toString(),
    FNumFormats.percent
  );
  const currentVotesFormatted = fNum2(
    scale(bnum(currentWeight.value), -4).toString(),
    FNumFormats.percent
  );
  return t(remainingVotesText, [
    remainingVotesFormatted,
    currentVotesFormatted,
    unallocatedVotesFormatted.value,
  ]);
});

const inputRules = [v => !v || isVoteWeightValid(v) || '', isPositive()];

/**
 * METHODS
 */
function isVoteWeightValid(voteWeight) {
  if (voteWeight === '') return true;
  const currentValue = scale(voteWeight, 2).toNumber();
  const isValid =
    currentValue <= props.unallocatedVoteWeight + Number(currentWeight.value);
  return isValid;
}

async function submitVote() {
  const totalVoteShares = scale(voteWeight.value, 2).toString();
  try {
    voteState.init = true;
    voteState.error = null;
    const tx = await gaugeControllerService.voteForGaugeWeights(
      props.gauge.address,
      BigNumber.from(totalVoteShares)
    );
    voteState.init = false;
    voteState.confirming = true;
    handleTransaction(tx);
  } catch (e) {
    console.error(e);
    const error = e as WalletError;
    voteState.init = false;
    voteState.confirming = false;
    voteState.error = {
      title: 'Vote failed',
      description: error.message,
    };
  }
}

async function handleTransaction(tx) {
  addTransaction({
    id: tx.hash,
    type: 'tx',
    action: 'voteForGauge',
    summary: t('veBAL.liquidityMining.popover.voteForGauge', [
      fNum2(scale(voteWeight.value, -2).toString(), FNumFormats.percent),
      props.gauge.pool.symbol,
    ]),
    details: {
      voteWeight: voteWeight.value,
    },
  });

  txListener(tx, {
    onTxConfirmed: async (receipt: TransactionReceipt) => {
      voteState.receipt = receipt;

      const confirmedAt = await getTxConfirmedAt(receipt);
      voteState.confirmedAt = dateTimeLabelFor(confirmedAt);
      voteState.confirmed = true;
      voteState.confirming = false;
      emit('success');
    },
    onTxFailed: () => {
      console.error('Vote failed');
      voteState.error = {
        title: 'Vote Failed',
        description: 'Vote failed for an unknown reason',
      };
      voteState.confirming = false;
    },
  });
}

/**
 * LIFECYCLE
 */
onMounted(() => {
  if (hasVotes.value) voteWeight.value = currentWeightNormalized.value;
});

return (_ctx: any,_cache: any) => {
  const _component_BalIcon = _resolveComponent("BalIcon")!
  const _component_BalCircle = _resolveComponent("BalCircle")!
  const _component_BalAlert = _resolveComponent("BalAlert")!
  const _component_BalAssetSet = _resolveComponent("BalAssetSet")!
  const _component_BalLink = _resolveComponent("BalLink")!
  const _component_BalBtn = _resolveComponent("BalBtn")!
  const _component_BalModal = _resolveComponent("BalModal")!

  return (_openBlock(), _createBlock(_component_BalModal, {
    show: "",
    fireworks: _unref(voteState).confirmed,
    onClose: _cache[2] || (_cache[2] = ($event: any) => (emit('close')))
  }, {
    header: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        (_unref(voteState).confirmed)
          ? (_openBlock(), _createBlock(_component_BalCircle, {
              key: 0,
              size: "8",
              color: "green",
              class: "mr-2 text-white"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_BalIcon, { name: "check" })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createElementVNode("h4", null, _toDisplayString(_unref(voteTitle)), 1)
      ])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", null, [
        (!_unref(voteWarning))
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
              _createElementVNode("ul", _hoisted_3, [
                _createElementVNode("li", _hoisted_4, _toDisplayString(_unref(t)('veBAL.liquidityMining.popover.emissionsInfo')), 1),
                _createElementVNode("li", _hoisted_5, _toDisplayString(_unref(t)('veBAL.liquidityMining.popover.resubmitVote')), 1),
                _createElementVNode("li", null, _toDisplayString(_unref(t)('veBAL.liquidityMining.popover.voteLockInfo', [
                _unref(voteLockedUntilText),
              ])), 1)
              ])
            ]))
          : _createCommentVNode("", true),
        (_unref(voteWarning))
          ? (_openBlock(), _createBlock(_component_BalAlert, {
              key: 1,
              type: "warning",
              title: _unref(voteWarning).title,
              description: _unref(voteWarning).description,
              class: "mb-4 w-full rounded"
            }, null, 8, ["title", "description"]))
          : _createCommentVNode("", true),
        (_unref(voteError))
          ? (_openBlock(), _createBlock(_component_BalAlert, {
              key: 2,
              type: "error",
              title: _unref(voteError).title,
              description: _unref(voteError).description,
              block: "",
              class: "mt-2 mb-4"
            }, null, 8, ["title", "description"]))
          : _createCommentVNode("", true),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_component_BalAssetSet, {
              logoURIs: __props.logoURIs,
              width: 100,
              size: 32
            }, null, 8, ["logoURIs"]),
            (__props.gauge.pool.name)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createElementVNode("p", _hoisted_9, _toDisplayString(__props.gauge.pool.name), 1),
                  _createElementVNode("p", _hoisted_10, _toDisplayString(__props.gauge.pool.symbol), 1)
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                  _createElementVNode("p", _hoisted_12, _toDisplayString(__props.gauge.pool.symbol), 1)
                ]))
          ]),
          _createVNode(_component_BalLink, {
            href: __props.poolURL,
            external: "",
            noStyle: "",
            class: "group flex items-center"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_BalIcon, {
                name: "arrow-up-right",
                class: "text-gray-500 transition-colors group-hover:text-pink-500"
              })
            ]),
            _: 1
          }, 8, ["href"])
        ]),
        _createVNode(BalForm, { class: "vote-form" }, {
          default: _withCtx(() => [
            _createVNode(BalTextInput, {
              modelValue: voteWeight.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (voteWeight.value = $event)),
              name: "voteWeight",
              type: "number",
              autocomplete: "off",
              autocorrect: "off",
              spellcheck: "false",
              step: "any",
              placeholder: "0",
              validateOn: "input",
              rules: inputRules,
              disabled: 
            _unref(voteInputDisabled) || _unref(transactionInProgress) || _unref(voteState).receipt
          ,
              size: "md",
              autoFocus: ""
            }, {
              append: _withCtx(() => [
                _hoisted_13
              ]),
              _: 1
            }, 8, ["modelValue", "disabled"]),
            (_unref(voteError))
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, _toDisplayString(_unref(t)('veBAL.liquidityMining.popover.warnings.noVeBal.inputHintText')), 1))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  class: _normalizeClass(['mt-2 text-sm'].concat(_unref(unallocatedVotesClass)))
                }, _toDisplayString(_unref(remainingVotes)), 3)),
            _createElementVNode("div", _hoisted_15, [
              (_unref(voteState).receipt)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _createVNode(ConfirmationIndicator, {
                      txReceipt: _unref(voteState).receipt,
                      class: "mb-2"
                    }, null, 8, ["txReceipt"]),
                    (_unref(voteState).receipt)
                      ? (_openBlock(), _createBlock(_component_BalBtn, {
                          key: 0,
                          color: "gray",
                          outline: "",
                          block: "",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('close')))
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('getVeBAL.previewModal.returnToVeBalPage')), 1)
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ], 64))
                : (_openBlock(), _createBlock(_component_BalBtn, {
                    key: 1,
                    color: "gradient",
                    block: "",
                    disabled: _unref(voteButtonDisabled),
                    loading: _unref(transactionInProgress),
                    loadingLabel: 
              _unref(voteState).init
                ? _ctx.$t('veBAL.liquidityMining.popover.actions.vote.loadingLabel')
                : _ctx.$t('veBAL.liquidityMining.popover.actions.vote.confirming')
            ,
                    onClick: _withModifiers(submitVote, ["prevent"])
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(voteButtonText)), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled", "loading", "loadingLabel", "onClick"]))
            ])
          ]),
          _: 1
        })
      ])
    ]),
    _: 1
  }, 8, ["fireworks"]))
}
}

})